import { Injectable } from '@angular/core';
import { LogError, LogStatus, Metadata, RunView } from '@memberjunction/core';
import { AskSkipMetadataEngine, ContactEntity, ContactEntityExtended, ContactRoleEntity, InstanceConnectorEntityExtended, InstanceContactEntity, InstanceContactEntityExtended, InstanceEntityExtended, OrganizationContactEntityExtended, OrganizationEntityExtended, PlanEntityExtended, SubscriptionEntityExtended } from 'mj_generatedentities';
import { BehaviorSubject, catchError, from, interval, switchMap, takeWhile, concatMap } from 'rxjs';
import { SharedService } from './shared-service';
import { Router } from '@angular/router';
import { User } from '@auth0/auth0-angular';
import { ADMIN_CONTACT_ROLE_NAME, buildRoutePrefix, GUEST_CONTACT_ROLE_NAME, OWNER_CONTACT_ROLE_NAME, STANDARD_CONTACT_ROLE_NAME, SYSTEM_ADMIN_ROLE_NAME } from '../util';

const INSTANCE_POLLING_INTERVAL = 30000; // Poll for instance deployment every 30 seconds
const CONNECTOR_POLLING_INTERVAL = 60000; // Poll for connector integration every 60 seconds
const MAX_INSTANCE_ATTEMPTS = 30; // Max attempts for instance deployment (10 minutes at 30s intervals)
const MAX_CONNECTOR_ATTEMPTS = 360; // Max attempts for connector integration (6 hours at 60s intervals)

export enum MetadataLoadingStage {
  NotStarted = 0,
  ContactLoaded = 1,
  OrganizationContactLoaded = 2,
  ContactRoleLoaded = 3,
  OrganizationLoaded = 4,
  SubscriptionsLoaded = 5,
  InstanceMetadataLoaded = 6,
  PlanLoaded = 7,
  InstanceConnectorsLoaded = 8
}

export interface OrganizationData {
  Name: string;
  Address?: string;
  City?: string;
  State?: string;
  Country?: string;
  PostalCode?: string;
  Phone?: string;
}

export interface CombinedInstanceData {
  Instance: InstanceEntityExtended;
  InstanceContact: InstanceContactEntityExtended;
}

@Injectable({
  providedIn: 'root'
})
export class UserContextService {
  private md: Metadata;

  private _organization: OrganizationEntityExtended;
  private _contactRole: ContactRoleEntity;
  private _contact: ContactEntityExtended;
  private _currentOrganizationContact: OrganizationContactEntityExtended;
  private _organizationContacts: OrganizationContactEntityExtended[];
  private _subscriptions: SubscriptionEntityExtended[];
  private _currentInstance: InstanceEntityExtended;
  private _pendingInstance: InstanceEntityExtended;
  private _instancesData: CombinedInstanceData[];
  private _currentPlan: PlanEntityExtended;
  private _instanceConnectors: InstanceConnectorEntityExtended[];

  private _metadataLoadingStageSource = new BehaviorSubject<MetadataLoadingStage>(MetadataLoadingStage.NotStarted);
  MetadataLoadingStage$ = this._metadataLoadingStageSource.asObservable();

  private _onboardingStateSource = new BehaviorSubject<'Preboarding' | 'Ongoing' | 'Complete'>('Ongoing');
  /** This is a flag indicating the state of the onboarding process. This can be in one of three states:
   *  Preboarding: The user is considering creating a new organization or instance and is at the beginning of the onboarding process.
   *  Situation 1: If the user is in an organization and is considering creating a new organization, they can access the new organization step.
   *  Situation 2: If the user is considering creating a new instance, they can access the pricing page.
   *  Ongoing: The user is in the middle of the onboarding process and is either creating an organization and/or instance.
   *  Complete: The user has completed the onboarding process and has an active organization and instance.
   */
  OnboardingState$ = this._onboardingStateSource.asObservable();

  private _isMetadataLoadingSource = new BehaviorSubject<boolean>(true);
  IsMetadataLoading$ = this._isMetadataLoadingSource.asObservable();

  constructor(private sharedService: SharedService, private router: Router) {
    this.md = new Metadata();
    this._organization = null;
    this._contact = null;
    this._contactRole = null;
    this._currentOrganizationContact = null;
    this._organizationContacts = [];
    this._subscriptions = [];
    this._instancesData = [];
    this._currentInstance = null;
    this._pendingInstance = null;
    this._currentPlan = null;
    this._instanceConnectors = [];
  }

  public get Organization(): OrganizationEntityExtended {
    return this._organization;
  }

  public get OrganizationContact(): OrganizationContactEntityExtended {
    return this._currentOrganizationContact;
  }

  public get OrganizationContacts(): OrganizationContactEntityExtended[] {
    return this._organizationContacts;
  }

  public get Subscriptions(): SubscriptionEntityExtended[] {
    return this._subscriptions;
  }

  public get Contact(): ContactEntityExtended {
    return this._contact;
  }

  public get ContactRole(): ContactRoleEntity {
    return this._contactRole;
  }

  public get CurrentInstance(): InstanceEntityExtended {
    return this._currentInstance;
  }

  public get PendingInstance(): InstanceEntityExtended {
    return this._pendingInstance;
  }

  public get Instances(): InstanceEntityExtended[] {
    return this._instancesData.map(i => i.Instance);
  }

  public get InstanceContacts(): InstanceContactEntity[] {
    return this._instancesData.map(i => i.InstanceContact);
  }

  public get CurrentPlan(): PlanEntityExtended {
    return this._currentPlan;
  }

  public get InstanceConnectors(): InstanceConnectorEntityExtended[] {
    return this._instanceConnectors;
  }

  public get MetadataLoadingStage(): MetadataLoadingStage {
    return this._metadataLoadingStageSource.getValue();
  }

  public get OnboardingState() {
    return this._onboardingStateSource.getValue();
  }

  /**
   * Gets the subscription entity that the current instance is owned by.
   * 
   */
  public get CurrentSubscription(): SubscriptionEntityExtended {
    if (this._currentInstance) {
      return this._subscriptions.find(s => s.ID === this._currentInstance.SubscriptionID);
    } else if (this._pendingInstance) { // Check pending instance if current instance is not set
      return this._subscriptions.find(s => s.ID === this._pendingInstance.SubscriptionID);
    }
    return null;
  }

  public GoToPreboarding(): void {
    this.setOnboardingState('Preboarding');
  }

  /**
   * ONLY USE THIS FUNCTION IN THE HEADER COMPONENT.
   * The purpose of this function is to allow the user to leave preboarding by setting 
   * the onboarding state to 'Complete'. This situation occurs when the user goes into
   * preboarding, with the 'intent' to create a new organization or instance, and then decides to leave
   * by navigating back to the settings, reports, or chat page. This essentially cancels
   * the onboarding process and sets the user back to the complete state.
   * 
   */
  public LeavePreboarding(): void {
    if (this.OnboardingState === 'Preboarding') {
      this.setOnboardingState('Complete');
    }
  }

  public async SetCurrentOrganizationContact(orgID: string): Promise<void> {
    if (this._currentOrganizationContact && this._currentOrganizationContact.OrganizationID === orgID) {
      return;
    }

    const oc = this._organizationContacts.find(oc => oc.OrganizationID === orgID);
    if (oc) {
      this._currentOrganizationContact = oc;

      // Set all the metadata dependent on the organization contact to null and reload
      this.setMetadataLoadingStage(MetadataLoadingStage.OrganizationContactLoaded, true);
      this._organization = null;
      this._contactRole = null;
      this._subscriptions = [];
      this._currentInstance = null;
      this._pendingInstance = null;
      this._instancesData = [];
      this._currentPlan = null;
      this._instanceConnectors = [];

      const p1 = this.markAccessDate(oc);
      const p2 = this.LoadMetaData();
      await Promise.all([p1, p2]);

      // Could have swapped to an organization that is in onboarding, so check and go to the correct stage
      if (this.OnboardingState === 'Ongoing') {
        await this.CheckOnboardingAndGoToStage();
      } else {
        const prefix = buildRoutePrefix(this.CurrentInstance.ID);
        this.router.navigate([prefix, 'chat']);
      }
    }
    else {
      throw new Error(`Failed to set current organization contact because organization with ID ${orgID} does not exist.`);
    }
  }

  public async SetCurrentInstance(instanceId: string): Promise<void> {
    if (this._currentInstance && this._currentInstance.ID === instanceId) {
      return;
    }

    const i = this.Instances.find(i => i.ID === instanceId);
    if (i) {
      this._currentInstance = i;

      await this.markAccessDate(this._instancesData.find(i => i.Instance.ID === instanceId).InstanceContact);

      // Set all the metadata dependent on the instance to null and reload
      this.setMetadataLoadingStage(MetadataLoadingStage.InstanceMetadataLoaded, true);
      this._currentPlan = null;
      this._instanceConnectors = [];

      await this.LoadMetaData();

      // Could have swapped to an instance that is in onboarding, so check and go to the correct stage
      if (this.OnboardingState === 'Ongoing') {
        await this.CheckOnboardingAndGoToStage();
      } else {
        const prefix = buildRoutePrefix(instanceId);
        this.router.navigate([prefix, 'chat']);
      }
    }
    else {
      throw new Error(`Failed to set current instance because instance with ID ${instanceId} does not exist.`);
    }
  }

  //
  // TEMPORARY - we will replace this with a proper check once we have the roles in place
  //
  private previousInstance: InstanceEntityExtended;
  private SystemAdminModeSource = new BehaviorSubject<boolean>(false);
  SystemAdminMode$ = this.SystemAdminModeSource.asObservable();
  public SetCurrentInstanceAsSuperAdmin(instance: InstanceEntityExtended): void {
    if (this._currentInstance && this._currentInstance.ID === instance.ID) {
      return;
    }

    if (this.IsContactInternalSuperAdmin) {
      this.previousInstance = this._currentInstance;
      this._currentInstance = instance;
      this.SystemAdminModeSource.next(true);
    }
  }
  public RestorePreviousInstance(): string {
    if (this.previousInstance && this.IsContactInternalSuperAdmin) {
      this._currentInstance = this.previousInstance;
      this.previousInstance = null;
      this.SystemAdminModeSource.next(false);
    }
    return this._currentInstance.ID;
  }

  private internalSuperAdminEmailDomains = ['bluecypress.io', 'tasio.co', 'memberjunction.com', 'askskip.ai'];
  public get IsContactInternalSuperAdmin(): boolean {
    // TEMPORARY - we will replace this with a proper check once we have the roles in place
    // current logic is to check to see if the Organization Name is Blue Cypress and if the contact has a domain that 
    // is bluecypress.io, tasio.co, memberjunction.com or askskip.ai
    // AND their contact is also an OWNER or ADMIN in the Blue Cypress org.   
    // This is a temporary check to allow us to test the onboarding process with a few users.

    if (this._contact && this._organization && this._organization.Name.trim().toLowerCase() === 'blue cypress') {
      const emailDomain = this._contact.Email.split('@')[1].trim().toLowerCase();
      if (this.internalSuperAdminEmailDomains.includes(emailDomain)) {
        return this._currentOrganizationContact.ContactRole.trim().toLowerCase() === 'owner' || 
               this._currentOrganizationContact.ContactRole.trim().toLowerCase() === 'admin';
      }
    }
    return false;
  }

  public async LoadMetaData(profile?: User, pendingUrl?: string, setGlobalLoading: boolean = true): Promise<void> {
    try {
      this.setIsMetadataLoading(true, setGlobalLoading);
      this.setOnboardingState('Ongoing');
  
      // Step 1: Load or create the contact
      if (!(await this.loadContact(profile?.email))) {
        await this.createContact(profile);
        return; // Exit early if contact is created
      }
  
      // Step 2: Load preferred organization contact
      if (!(await this.loadPreferredOrganizationContact(pendingUrl))) {
        return; // Exit if loading fails
      }
  
      // Step 3: Load organization and contact role in parallel
      const [roleLoaded, organizationLoaded] = await Promise.all([
        this.loadContactRole(this._currentOrganizationContact.ContactRoleID),
        this.loadOrganization(this._currentOrganizationContact.OrganizationID)
      ]);
  
      if (!organizationLoaded || !roleLoaded) return; // Exit if either fails

      // Step 4: Load Subscriptions
      if (!(await this.loadSubscriptions())) return; // Exit if subscriptions fail
  
      // Step 5: Load instance metadata
      if (!(await this.loadInstanceMetadata(pendingUrl))) return; // Exit if instance metadata fails
  
      // Step 6: Load current plan and active instance connectors
      const [planLoaded, instanceConnectorsLoaded] = await Promise.all([
        this.loadCurrentPlan(),
        this.loadActiveInstanceConnectors(),
      ]);
      if (!planLoaded || !instanceConnectorsLoaded) return; // Exit if either fails
  
      // If all steps complete successfully, mark onboarding as complete
      this.setOnboardingState('Complete');
    } catch (e) {
      LogError(e.message);
      this.sharedService.NoAccessMessage = e.message;
      this.sharedService.AccessDenied = true;
    } finally {
      this.setIsMetadataLoading(false, setGlobalLoading);
    }
  }
  

  public async CheckOnboardingAndGoToStage(): Promise<void> {
    if (this.OnboardingState === 'Complete') {
      return;
    }

    const stage = this.MetadataLoadingStage;

    try {
      this.sharedService.markGlobalLoading(true);
      if (stage < MetadataLoadingStage.OrganizationLoaded) {
        this.router.navigate(['/onboarding/new-organization']); // User needs to create organization
      } else {
        if (stage >= MetadataLoadingStage.InstanceMetadataLoaded) {
          if (stage < MetadataLoadingStage.InstanceConnectorsLoaded) {
            if (await this.CheckPendingInstaceConnectors()) {
              this.router.navigate(['/onboarding/completed']); // Need to wait for deployment
            } else {
              this.router.navigate(['/onboarding/connector']); // User of organization needs to choose connectors
            }
          }
        } else if (this.DoesOrganizationHaveActiveInstances() && this.ContactRole.Name !== OWNER_CONTACT_ROLE_NAME) {
          this.setOnboardingState('Complete'); // Set to 'Complete' because the user just doesn't have access to an instance and is not attempting to create one.
          this.router.navigate(['/dashboard/no-instance']); // User is not assigned to any active instance owned by the organization and is not the owner of the organization
        } else {
          if (await this.LoadPendingInstance()) {
            if (await this.CheckPendingInstaceConnectors()) {
              this.router.navigate(['/onboarding/completed']); // Need to wait for deployment
            } else {
              this.router.navigate(['/onboarding/connector']); // User of organization needs to choose connectors
            }
          } else {
            this.router.navigate(['/onboarding/pricing']); // User of organization needs to choose subscription to trigger deployment
          }
        }
      }
    } catch (e) {
      LogError('Error during onboarding check:', e);
      this.sharedService.NoAccessMessage = e.message;
      this.sharedService.AccessDenied = true;
    } finally {
      this.sharedService.markGlobalLoading(false);
    }
  }

  public TriggerDeploymentSearch(): void {
    const instancePollingIntervalMs = INSTANCE_POLLING_INTERVAL;
    const connectorPollingIntervalMs = CONNECTOR_POLLING_INTERVAL; 
    const maxInstanceAttempts = MAX_INSTANCE_ATTEMPTS; 
    const maxConnectorAttempts = MAX_CONNECTOR_ATTEMPTS; 
  
    let instanceAttempts = 0;
    let connectorAttempts = 0;
  
    // Stage 1: Poll for instance deployment
    interval(instancePollingIntervalMs)
      .pipe(
        switchMap(() => {
          instanceAttempts++;
          // @jordanfanapour ---- should we await the NavigationStart event here like we do in the app.component.ts - or for onboarding perhaps doesn't matter? 
          return from(this.loadInstanceMetadata(this.router.url));
        }),
        takeWhile((isInstanceLoaded: boolean) => !isInstanceLoaded && instanceAttempts < maxInstanceAttempts, true), 
        concatMap((isInstanceLoaded: boolean) => {
          if (isInstanceLoaded) {

            LogStatus('Instance successfully deployed. Starting connector integration check.');
            this.setMetadataLoadingStage(MetadataLoadingStage.InstanceMetadataLoaded);
            // Stage 2: Poll for connector integration
            return interval(connectorPollingIntervalMs).pipe(
              switchMap(() => {
                connectorAttempts++;
                return from(this.loadActiveInstanceConnectors());
              }),
              takeWhile((areConnectorsLoaded: boolean) => !areConnectorsLoaded && connectorAttempts < maxConnectorAttempts, true)
            );

          } else {
            LogError('Instance deployment timed out.');
            throw new Error('Instance deployment timed out.');
          }

        }),
        catchError(error => {
          LogError('Error during deployment or connector integration:', error);
          throw error;
        })

      )
      .subscribe({
        next: (areConnectorsLoaded: boolean) => {
          if (areConnectorsLoaded) {
            // Stage 3: Onboarding complete
            LogStatus('Connectors successfully integrated. Onboarding complete.');
            this.setMetadataLoadingStage(MetadataLoadingStage.InstanceConnectorsLoaded);
            this.setOnboardingState('Complete');

          }
        },
        error: () => {
          LogError('Deployment search failed.');
        },
        complete: () => {
          if (connectorAttempts >= maxConnectorAttempts) {
            LogError('Connector integration timed out.');
          }
        }
      });
  }

  public async SetUpNewOrganizaton(data: OrganizationData): Promise<void> {
    if (!this._contact) {
      throw new Error('Contact does not exist');
    }

    if (this._organization) {
      // If the organization already exists, then the user is already part of one and
      // we are creating a new one. So we need wipe the current loaded data to continue with
      // onboarding a new organization.
      this.setOnboardingState('Ongoing');
      this.setMetadataLoadingStage(MetadataLoadingStage.ContactLoaded, true);
      this._currentOrganizationContact = null;
      this._organizationContacts = [];
      this._subscriptions = [];
      this._instancesData = [];
      this._organization = null;
      this._contactRole = null;
      this._currentInstance = null;
      this._pendingInstance = null;
      this._instanceConnectors = [];
    }

    try {
      await this.createOrganization(data);
      this._contactRole = this.OwnerRole;

      if (this._organization && this._contactRole) {
        await this.createOrganizationContact(this._contact.ID, this._organization.ID, this._contactRole.ID);
      } else {
        throw new Error('Organization and contact role are null after creation');
      }

    } catch (e) {
      throw new Error(`Failed to set up new organization\n${e.message}`);
    }
  }

  public async PrepareForNewInstance(): Promise<void> {
    if (!this._organization) {
      throw new Error('Could not prepare for new instance because the organization is not set.');
    }

    if (this._currentInstance || this._pendingInstance) {
      this.setOnboardingState('Ongoing');
      this.setMetadataLoadingStage(MetadataLoadingStage.OrganizationLoaded, true);
      this._currentInstance = null;
      this._pendingInstance = null;
      this._instanceConnectors = [];
    }
  }

  public async LoadPendingInstance(): Promise<boolean> {
    if (!this._organization) {
      throw new Error('Could not check pending instance request because the organization is not set.');
    }

    const rv = new RunView();
    const result = await rv.RunView({
      EntityName: 'Instances',
      ExtraFilter: `OrganizationID = '${this._organization.ID}' AND Status = 'Pending'`,
    }, this.md.CurrentUser);

    if (result.Success && result.Results.length > 0) {
      this._pendingInstance = result.Results[0];
      return true;
    }

    return false; 
  }
   
  public async CheckPendingInstaceConnectors(): Promise<boolean> {
    if (!this._currentInstance && !this._pendingInstance) { // if we don't have a current instance or a pending instance then we can't check for pending connectors
      throw new Error('Could not check pending instance connectors because the instance is not set.');
    }

    // If instance is deployed, then use the current instance, otherwise use the pending instance
    const instanceID = this._currentInstance ? this._currentInstance.ID : this._pendingInstance.ID;

    const rv = new RunView();
    const result = await rv.RunView({
      EntityName: 'Instance Connectors',
      ExtraFilter: `InstanceID = '${instanceID}' AND 
                    (Status = 'Pending' OR Status = 'Deploying' OR Status = 'Provisioning')`,
    }, this.md.CurrentUser);

    if (result.Success && result.Results.length > 0) {
      return true;
    }

    return false;
  }

  /**
   * Used to check if the current organization has any active instances.
   * Intended use for contacts within an organization but have not yet been assigned to an instance.
   * 
   * @returns True if the organization has an active instance, false otherwise
   */
  public async DoesOrganizationHaveActiveInstances(): Promise<boolean> {
    const rv = new RunView();
    const result = await rv.RunView({
      EntityName: 'Instances',
      ExtraFilter: `OrganizationID = '${this._organization.ID}' AND Status = 'Active'`,
      ResultType: 'count_only'
    }, this.md.CurrentUser);

    if (result.Success && result.RowCount > 0) {
      return true;
    }

    return false;
  }

  /**
   * Used to get all the active contacts for the current organization.
   * Intended use for the teams page.
   * 
   * @returns The rows of a view of all the active organization contacts for the current organization
   */
  public async GetActiveOrganizationContacts(): Promise<any[]> {
    const systemAdminRole = await this.SystemAdminRole;
    const rv = new RunView();
    const result = await rv.RunView({
      EntityName: 'Organization Contacts',
      ExtraFilter: `OrganizationID = '${this._organization.ID}' AND Active = 1 AND ContactRoleID != '${systemAdminRole.ID}'`,
      OrderBy: 'Contact ASC',
    }, this.md.CurrentUser);

    if (result.Success) {
      return result.Results;
    }

    return [];
  }

  /**
   * Used to get all the organizations that the current contact is part of.
   * Intended use for the organizations page.
   * 
   * @returns An array of OrganizationEntityExtended objects that the current contact is part of
   */
  public async GetOrganizationsOfContact(): Promise<OrganizationEntityExtended[]> {
    if (!this._organizationContacts) {
      this.sharedService.DisplayNotification('Contact not found!', 'error');
      this.sharedService.AccessDenied = true;
    }

    const rv = new RunView();
    const result = await rv.RunView<OrganizationEntityExtended>({
      EntityName: 'Organizations',
      ExtraFilter: `ID IN (${this._organizationContacts.map(oc => `'${oc.OrganizationID}'`).join(',')})`,
      ResultType: 'entity_object'
    }, this.md.CurrentUser);

    if (result.Success) {
      return result.Results;
    }

    return [];
  }

  /**
   * Used to get all the instances that the current Organization owns.
   * Intended use for the instances page.
   * 
   * @returns An array of InstanceEntityExtended objects that the current organization owns
   */
  public async GetInstancesOfOrganization(): Promise<InstanceEntityExtended[]> {
    if (!this._organization) {
      this.sharedService.DisplayNotification('Organization not found!', 'error');
      this.sharedService.AccessDenied = true;
    }

    const rv = new RunView();
    const result = await rv.RunView<InstanceEntityExtended>({
      EntityName: 'Instances',
      ExtraFilter: `OrganizationID = '${this._organization.ID}' AND Status != 'Archived'`,
      ResultType: 'entity_object'
    }, this.md.CurrentUser);

    if (result.Success) {
      return result.Results;
    }

    return [];
  }

  /**
   * Sets the Active flag to false for the Organization Contact corresponding to the current
   * contact and the given organization. Setting the Active flag to false effectively removes
   * the contact from the organization.
   * 
   * @param organizationID The ID of the organization to leave for the current contact
   */
  public async LeaveOrganization(organizationID: string): Promise<void> {
    if (!this._organizationContacts) {
      this.sharedService.DisplayNotification('Contact not found!', 'error');
      this.sharedService.AccessDenied = true;
    }

    const orgContact = this._organizationContacts.find(oc => oc.OrganizationID === organizationID);
    if (!orgContact) {
      throw new Error(`Failed to leave organization because contact is not part of organization with ID ${organizationID}`);
    }

    orgContact.Active = false;
    if (!(await orgContact.Save())) {
      throw new Error('Failed to leave organization:\n' + orgContact.LatestResult.Errors[0].Message);
    }

    // If the current organization contact is the one we are leaving, then we need to reset the metadata
    if (this._currentOrganizationContact.OrganizationID === organizationID) {
      this.setMetadataLoadingStage(MetadataLoadingStage.ContactLoaded, true);
      this._currentOrganizationContact = null;
      this._organizationContacts = [];
      this._subscriptions = [];
      this._organization = null;
      this._contactRole = null;
      this._currentInstance = null;
      this._pendingInstance = null;
      this._instanceConnectors = [];

      await this.LoadMetaData();

      if (this.OnboardingState === 'Ongoing') {
        await this.CheckOnboardingAndGoToStage();
      }
    } else {
      this._organizationContacts = this._organizationContacts.filter(oc => oc.OrganizationID !== organizationID);
    }
  }

  public GetContactRoles(): ContactRoleEntity[] {
    return AskSkipMetadataEngine.Instance.ContactRoles.filter(cr => cr.Name !== SYSTEM_ADMIN_ROLE_NAME);
  }

  public get OwnerRole(): ContactRoleEntity {
    return AskSkipMetadataEngine.Instance.OwnerContactRole;
  }

  public get AdminRole(): ContactRoleEntity {
    return AskSkipMetadataEngine.Instance.AdminContactRole;
  }

  public get StandardRole(): ContactRoleEntity {
    return AskSkipMetadataEngine.Instance.StandardContactRole;
  }

  public get GuestRole(): ContactRoleEntity {
    return AskSkipMetadataEngine.Instance.GuestContactRole;
  }

  public get SystemAdminRole(): ContactRoleEntity {
    return AskSkipMetadataEngine.Instance.SystemAdminContactRole;
  }

  private async loadContact(email?: string): Promise<boolean> {
    if (this._contact) {
      return true;
    }

    const rv = new RunView();

    const result = await rv.RunView<ContactEntity>({
      EntityName: 'Contacts',
      ExtraFilter: `Email = '${email}'`,
      ResultType: 'entity_object'
    }, this.md.CurrentUser);

    if (result.Success && result.Results.length > 0) {
      this._contact = result.Results[0];
      this.setMetadataLoadingStage(MetadataLoadingStage.ContactLoaded);

      LogStatus('Contact loaded successfully.');
      return true;
    }

    return false;
  }

  private async loadPreferredOrganizationContact(pendingUrl?: string): Promise<boolean> {
    if (!this._contact) {
      throw new Error('Could not load preferred organization contact because the contact is not set.');
    }

    if (this._currentOrganizationContact) {
      return true;
    }

    // IMPORTANT: We need to filter out the system admin role from the list of organization contacts
    const systemAdminRole = this.SystemAdminRole;
    const rv = new RunView();
    const result = await rv.RunView<OrganizationContactEntityExtended>({
      EntityName: 'Organization Contacts',
      ExtraFilter: `ContactID = '${this._contact.ID}' AND Active = 1 AND ContactRoleID != '${systemAdminRole.ID}'`,
      OrderBy: 'LastAccessedAt DESC',
      ResultType: 'entity_object'
    }, this.md.CurrentUser);

    if (result.Success && result.Results.length > 0) {
      const orgContacts = result.Results;
      const instanceID = this.getInstanceIDFromUrl(pendingUrl);

      // If the user is trying to access a particular instance, then find the organization contact of the organization that it belongs to
      if (instanceID) {
        const instance = await this.loadInstance(instanceID);
        const orgContact = orgContacts.find(oc => oc.OrganizationID === instance.OrganizationID);
        if (orgContact) {
          this._currentOrganizationContact = orgContact;
          this._currentInstance = instance;
        }
      } else {
        // If the user is not trying to access a particular instance, then just use the first organization contact
        this._currentOrganizationContact = orgContacts[0];
      }

      this._organizationContacts = orgContacts;

      // Mark the last accessed date
      await this.markAccessDate(this._currentOrganizationContact);

      LogStatus('Organization contact loaded successfully.');
      this.setMetadataLoadingStage(MetadataLoadingStage.OrganizationContactLoaded);
      return true;
    }

    return false;
  }

  private loadContactRole(contactRoleID: string): boolean {
    if (this._contactRole) {
      return true;
    }

    const role = this.GetContactRoles().find(cr => cr.ID === contactRoleID);

    if (role) {
      this._contactRole = role;
      this.setMetadataLoadingStage(MetadataLoadingStage.ContactRoleLoaded);

      LogStatus('Contact role loaded successfully.');
      return true;
    }

    throw new Error('Failed to load contact role:\n');
  }

  private async loadOrganization(organizationID: string): Promise<boolean> {
    if (this._organization) {
      return true;
    }
  
    const e = await this.md.GetEntityObject<OrganizationEntityExtended>('Organizations', this.md.CurrentUser);
    const loadResult = await e.Load(organizationID);

    if (loadResult) {
      this._organization = e;
      this.setMetadataLoadingStage(MetadataLoadingStage.OrganizationLoaded);

      LogStatus('Organization loaded successfully.');
      return true;
    }

    throw new Error('Failed to load organization:\n' + e.LatestResult.Errors[0].Message);
  }

  private async loadSubscriptions(): Promise<boolean> {
    if (!this._organization) {
      throw new Error('Could not load subscriptions because the organization is not set.');
    }

    if (this._subscriptions.length > 0) {
      return true;
    }

    const rv = new RunView();
    const result = await rv.RunView<SubscriptionEntityExtended>({
      EntityName: 'Subscriptions',
      ExtraFilter: `OrganizationID = '${this._organization.ID}'`, //AND Status = 'Active'`,
      ResultType: 'entity_object'
    }, this.md.CurrentUser);

    if (result.Success && result.Results.length > 0) {
      this._subscriptions = result.Results;

      LogStatus('Subscriptions loaded successfully.');
      this.setMetadataLoadingStage(MetadataLoadingStage.SubscriptionsLoaded);
      return true;
    }

    return false;
  }

  private async loadInstance(instanceID: string): Promise<InstanceEntityExtended> {
    const e = await this.md.GetEntityObject<InstanceEntityExtended>('Instances', this.md.CurrentUser);
    const loadResult = await e.Load(instanceID);

    if (loadResult) {
      return e;
    }

    throw new Error('Failed to load instance:\n' + e.LatestResult.Errors[0].Message);
  }

  private async loadInstances(): Promise<InstanceEntityExtended[]> {
    if (!this._organization) {
      throw new Error('Could not load instances because the organization is not set.');
    }

    const rv = new RunView
    const result = await rv.RunView<InstanceEntityExtended>({
      'EntityName': 'Instances',
      'ExtraFilter': `OrganizationID = '${this._organization.ID}' AND 
                      Status = 'Active'`,
      'ResultType': 'entity_object'
    });

    const instances = result.Results

    return instances;
  }

  private async loadInstanceContacts(): Promise<InstanceContactEntityExtended[]> {
    if (!this._contact) {
      throw new Error('Could not load instance contacts because the contact is not set.');
    }

    const rv = new RunView();
    const result = await rv.RunView<InstanceContactEntityExtended>({
      EntityName: 'Instance Contacts',
      ExtraFilter: `ContactID = '${this._contact.ID}' AND IsActive = 1`,
      ResultType: 'entity_object'
    });

    const instanceContacts = result.Results;

    const dateFilteredInstanceContacts = instanceContacts.filter(ic => {
      const currentDate = new Date();
      return (ic.StartsAt === null || ic.StartsAt <= currentDate) &&
             (ic.EndsAt === null || ic.EndsAt >= currentDate);
    });

    return dateFilteredInstanceContacts.sort((a, b) => {
      return new Date(b.LastAccessedAt).getTime() - new Date(a.LastAccessedAt).getTime();
    });
  }

  private filterInstanceWithActiveSubscriptions(instances: InstanceEntityExtended[]): InstanceEntityExtended[] {
    if (!this.Subscriptions) {
      throw new Error('Could not filter instances because the subscriptions are not set.');
    }

    const activeSubscriptionIDs = this.Subscriptions.filter(s => s.Status === 'Active').map(s => s.ID);
    return instances.filter(i => activeSubscriptionIDs.includes(i.SubscriptionID));
  }

  private mergeInstanceData(instances: InstanceEntityExtended[], instanceContacts: InstanceContactEntityExtended[]): CombinedInstanceData[] {
    // filter out the instance contacts that are not part of this organization
    instanceContacts = instanceContacts.filter(ic => instances.find(i => i.ID === ic.InstanceID));
    // filter out the instances that are not in the list of active instance contacts
    instances = instances.filter(i => instanceContacts.find(ic => ic.InstanceID === i.ID));

    const merged = instances.map(i => { 
      return { 
        Instance: i, 
        InstanceContact: instanceContacts.find(ic => ic.InstanceID === i.ID)
      };
    });

    return merged.filter(i => i.InstanceContact);
  }

  private async loadInstanceMetadata(pendingUrl?: string): Promise<boolean> {
    if (this.CurrentInstance && this._instancesData.length > 0) {
      return true;
    }

    const p1 = this.loadInstances();
    const p2 = this.loadInstanceContacts();

    let [instances, instanceContacts] = await Promise.all([p1, p2]);

    try {
      instances = this.filterInstanceWithActiveSubscriptions(instances);
    } catch (e) {
      LogError('Error filtering instances with active subscriptions:', e);
      throw new Error('Failed to filter instances with active subscriptions');
    }

    if (instances.length > 0) {
      this._instancesData = this.mergeInstanceData(instances, instanceContacts);

      let instance: InstanceEntityExtended = null;
      let instanceContact: InstanceContactEntity = null;
      // check to see if the pending route has an /instance and /instance/:instanceId segment and if so, then we should find that if we have it avalable in the list and then make that the current instance
      const instanceID = this.getInstanceIDFromUrl(pendingUrl);
      if (instanceID) {
        instance = this._instancesData.find(i => i.Instance.ID === instanceID).Instance;
        instanceContact = this._instancesData.find(ic => ic.Instance.ID === instanceID).InstanceContact;
      }

      // we will just grab the first instance in the list which is the most recently accessed based on the instance contact record
      if (!instance && this._instancesData.length > 0) {
        const instanceData = this._instancesData.reduce((max, i) => (i.InstanceContact.LastAccessedAt > max.InstanceContact.LastAccessedAt ? i : max), this._instancesData[0]);
        instance = instanceData.Instance;
        instanceContact = instanceData.InstanceContact;
      }

      if (!instance && this._instancesData.length > 0) {
        // if we still don't have an instance then we will just grab the first one in the list
        // this means we are an owner or admin and we have access to all instances
        instance = this._instancesData[0].Instance;
        instanceContact = this._instancesData[0].InstanceContact
      } 

      if (!instance) {
        return false;
      }

      this._currentInstance = instance;
      // Mark the last accessed date
      await this.markAccessDate(instanceContact);

      LogStatus('Instance metadata loaded successfully.');
      this.setMetadataLoadingStage(MetadataLoadingStage.InstanceMetadataLoaded);
      return true;
    }
    // else {
    //   this.sharedService.DisplayNotification('No active instances found for you!', 'warning');
    // }

    return false;
  }

  private async loadCurrentPlan(): Promise<boolean> {
    if (!this._currentInstance && !this._pendingInstance) {
      throw new Error('Could not load instance plan because the instance is not set.');
    }

    if (this._currentPlan) {
      return true;
    }

    const subscription = this.CurrentSubscription;
    const plan = await subscription.GetPlan();

    if (subscription && plan) {
      this._currentPlan = plan;
      this.setMetadataLoadingStage(MetadataLoadingStage.PlanLoaded);
      LogStatus('Current plan loaded successfully.');
      return true;
    }

    return false;
  }

  private async loadActiveInstanceConnectors(): Promise<boolean> {
    if (!this.CurrentInstance) {
      throw new Error('Could not load active instance connectors because the instance is not set.');
    } 
    else if (this._instanceConnectors.length > 0) {
      return true;
    }

    const rv = new RunView();
    const result = await rv.RunView<InstanceConnectorEntityExtended>({
      EntityName: 'Instance Connectors',
      ExtraFilter: `InstanceID = '${this.CurrentInstance.ID}'`,
      ResultType: 'entity_object'
    });

    const instanceConnectors = result.Results.filter(
      ic => ic.InstanceID === this.CurrentInstance.ID && (ic.Status === 'Active' || ic.Status === 'Syncing') // Only load active or syncing connectors
    );

    if (instanceConnectors.length > 0) {
      this._instanceConnectors = instanceConnectors;
      LogStatus('Active instance connectors loaded successfully.');
      this.setMetadataLoadingStage(MetadataLoadingStage.InstanceConnectorsLoaded);
      return true;
    }

    return false; // an instance can have zero connectors and it is still valid, we let the user add connectors via settings. This isn't likely to happen but it is possible we have someone onboard without doing this step.
  }

  private async createContact(profile: User): Promise<void> {
    if (this.isEmailValid(profile.email)) {
      const sanitizedEmail = profile.email?.replace(/'/g, "''"); // Escape single quotes

      const contact = await this.md.GetEntityObject<ContactEntity>('Contacts', this.md.CurrentUser);
      contact.NewRecord();
      contact.FirstName = profile.name || "";
      contact.LastName = profile.family_name || "";
      contact.Email = sanitizedEmail;
      contact.EmailVerified = profile.email_verified;
      contact.Phone = profile.phone_number;

      const saveResult = await contact.Save();
      if (saveResult) {
        this._contact = contact;
        this.setMetadataLoadingStage(MetadataLoadingStage.ContactLoaded);
      } else {
        throw new Error('Failed to create contact:\n' + contact.LatestResult.Errors[0].Message);
      }
    } else {
      throw new Error('Failed to create contact because email is invalid.');
    }
  }

  private async createOrganization(data: OrganizationData): Promise<void> {
    const organization = await this.md.GetEntityObject<OrganizationEntityExtended>('Organizations', this.md.CurrentUser);
    organization.NewRecord();

    organization.Name = data.Name;
    organization.AddressLine1 = data.Address;
    organization.City = data.City;
    organization.State = data.State;
    organization.Country = data.Country;
    organization.PostalCode = data.PostalCode;
    organization.Phone = data.Phone;

    const saveResult = await organization.Save();
    if (saveResult) {
      this._organization = organization;
      this.setMetadataLoadingStage(MetadataLoadingStage.OrganizationLoaded);
    } else {
      throw new Error('Failed to create organization:\n' + organization.LatestResult.Errors[0].Message);
    }
  }

  private async createOrganizationContact(contactID: string, organizationID: string, contactRoleID: string): Promise<void> {
    const organizationContact = await this.md.GetEntityObject<OrganizationContactEntityExtended>('Organization Contacts', this.md.CurrentUser);
    organizationContact.NewRecord();

    organizationContact.ContactID = contactID;
    organizationContact.OrganizationID = organizationID;
    organizationContact.ContactRoleID = contactRoleID;
    organizationContact.LastAccessedAt = new Date();
    organizationContact.Active = true;

    const saveResult = await organizationContact.Save();
    if (saveResult) {
      this._currentOrganizationContact = organizationContact;
      this._organizationContacts.push(organizationContact);
      this.setMetadataLoadingStage(MetadataLoadingStage.OrganizationContactLoaded);
    } else {
      throw new Error('Failed to create organization contact:\n' + organizationContact.LatestResult.Errors[0].Message);
    }
  }

  private async markAccessDate(entity: OrganizationContactEntityExtended | InstanceContactEntity): Promise<boolean> {
    if (!entity) {
      return false;
    }

    entity.LastAccessedAt = new Date();

    if (!(await entity.Save())) {
      console.error('Failed to mark last accessed date:', entity.LatestResult.Message);
      return false;
    }

    return true;
  }

  private isEmailValid(email: string | undefined | null): boolean {
    return !!email && email.trim().length > 0;
  }

  private getInstanceIDFromUrl(url: string): string {
    if (url && url.toLowerCase().includes('/instance')) {
      // the format of the URL is going to include /instance and the segment immediately after the /instance is the instanceId, and that
      // can be anywhere within the string so must find it and then use it to set the current instance
      const segments = url.split('/');
      const instanceSegmentIndex = segments.findIndex(s => s.toLowerCase() === 'instance'); 
      const instanceId = segments[instanceSegmentIndex + 1];
      return instanceId;
    }
    return '';
  }

  private setOnboardingState(value: 'Preboarding' | 'Ongoing' | 'Complete') {
    this._onboardingStateSource.next(value);
  }

  private setIsMetadataLoading(value: boolean, setGlobalLoading: boolean = true) {
    this._isMetadataLoadingSource.next(value);
    if (setGlobalLoading) {
      this.sharedService.markGlobalLoading(value);
    }
  }

  private setMetadataLoadingStage(value: MetadataLoadingStage, force: boolean = false) {
    if (force) {
      this._metadataLoadingStageSource.next(value);
    } else if (value > this.MetadataLoadingStage) {
      this._metadataLoadingStageSource.next(value);
    }
  }
}